import { debounce } from "lodash";
import {
  MotionStyle,
  useMotionValue,
  useMotionValueEvent,
  useTransform,
} from "motion/react";
import { useMemo, useRef, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useCompanyScopeContext, useRtl } from "@ryde/redux";
import {
  DRAG_HANDLE_WIDTH,
  drawersAtom,
  getRideDrawerMaxContentWidth,
  IRideDrawerDragState,
  MULTIPLE_DRAWER_OFFSET_WIDTH,
  RIDE_DRAWER_DEFAULT_FORM_WIDTH,
  RIDE_DRAWER_DEFAULT_MAP_WIDTH,
  RIDE_DRAWER_MAP_RESIZE_THROTTLE_MS,
  RIDE_DRAWER_MIN_FORM_WIDTH,
  RIDE_DRAWER_MIN_MAP_WIDTH,
  RIDE_DRAWER_RESIZE_KEYBOARD_STEP,
  RIDE_DRAWER_TOTAL_HANDLE_WIDTH,
  type GlobalAppEvents,
  type IFormMapDrawerResizeConfig,
} from "@ryde/shared";

export const useFormMapDrawerResize = ({
  drawerNumber = 0,
  mapResizeEvent,
  resizeConfig,
}: {
  drawerNumber?: number;
  /** Event to emit when map panel is resized (e.g. for Leaflet invalidateSize). */
  mapResizeEvent?: keyof GlobalAppEvents;
  resizeConfig?: IFormMapDrawerResizeConfig;
} = {}) => {
  const defaultFormWidth =
    resizeConfig?.defaultFormWidth ?? RIDE_DRAWER_DEFAULT_FORM_WIDTH;
  const defaultSecondaryWidth =
    resizeConfig?.defaultSecondaryWidth ?? RIDE_DRAWER_DEFAULT_MAP_WIDTH;
  const minFormWidth = resizeConfig?.minFormWidth ?? RIDE_DRAWER_MIN_FORM_WIDTH;
  const minSecondaryWidth =
    resizeConfig?.minSecondaryWidth ?? RIDE_DRAWER_MIN_MAP_WIDTH;
  const totalHandleWidth =
    resizeConfig?.totalHandleWidth ?? RIDE_DRAWER_TOTAL_HANDLE_WIDTH;
  const resizeKeyboardStep =
    resizeConfig?.resizeKeyboardStep ?? RIDE_DRAWER_RESIZE_KEYBOARD_STEP;
  const getMaxContentWidth =
    resizeConfig?.getMaxContentWidth ?? getRideDrawerMaxContentWidth;
  const { isRtl } = useRtl();
  const { eventEmitter } = useCompanyScopeContext();
  const drawers = useRecoilValue(drawersAtom);

  const formWidthMotion = useMotionValue(defaultFormWidth);
  const mapWidthMotion = useMotionValue(defaultSecondaryWidth);

  const [isMapVisible, setMapVisible] = useState(defaultSecondaryWidth > 0);
  const isMapVisibleRef = useRef(isMapVisible);
  useMotionValueEvent(mapWidthMotion, "change", (v: number) => {
    const visible = v > 0;
    if (visible !== isMapVisibleRef.current) {
      isMapVisibleRef.current = visible;
      setMapVisible(visible);
    }
  });

  const formDragRef = useRef<IRideDrawerDragState | null>(null);
  const mapDragRef = useRef<IRideDrawerDragState | null>(null);

  const pendingFormWidthRef = useRef<number | null>(null);
  const pendingMapWidthRef = useRef<number | null>(null);
  const animationFrameFormRef = useRef<number | null>(null);
  const animationFrameMapRef = useRef<number | null>(null);

  const drawersOffset = Math.max(0, (drawers?.length ?? 1) - 1 - drawerNumber);

  const gridTemplateColumnsStyle = useTransform(() => {
    const f = formWidthMotion.get();
    const m = mapWidthMotion.get();
    return `${DRAG_HANDLE_WIDTH}px ${f}px ${DRAG_HANDLE_WIDTH}px ${m}px`;
  });

  const drawerWidthStyle = useTransform(() => {
    const f = formWidthMotion.get();
    const m = mapWidthMotion.get();
    const total =
      f + m + totalHandleWidth + drawersOffset * MULTIPLE_DRAWER_OFFSET_WIDTH;
    return `${total}px`;
  });

  const flushFormWidth = useEvent(() => {
    animationFrameFormRef.current = null;
    if (pendingFormWidthRef.current !== null) {
      formWidthMotion.set(pendingFormWidthRef.current);
      pendingFormWidthRef.current = null;
    }
  });

  const flushMapWidth = useEvent(() => {
    animationFrameMapRef.current = null;
    if (pendingMapWidthRef.current !== null) {
      mapWidthMotion.set(pendingMapWidthRef.current);
      pendingMapWidthRef.current = null;
    }
  });

  const emitMapResize = useMemo(
    () =>
      mapResizeEvent
        ? debounce(
            () => eventEmitter?.emit(mapResizeEvent),
            RIDE_DRAWER_MAP_RESIZE_THROTTLE_MS,
          )
        : () => {},
    [eventEmitter, mapResizeEvent],
  );

  const computeWidthDelta = (startX: number, currentX: number): number => {
    const deltaX = currentX - startX;
    return isRtl ? deltaX : -deltaX;
  };

  const handleFormDrag = (event: MouseEvent) => {
    if (!formDragRef.current) {
      return;
    }

    const { startX, initialWidth } = formDragRef.current;
    const delta = computeWidthDelta(startX, event.clientX);
    const mapWidth = mapWidthMotion.get();
    const maxFormWidth = getMaxContentWidth() - mapWidth - totalHandleWidth;

    pendingFormWidthRef.current = Math.max(
      minFormWidth,
      Math.min(maxFormWidth, initialWidth + delta),
    );
    if (animationFrameFormRef.current === null) {
      animationFrameFormRef.current = requestAnimationFrame(flushFormWidth);
    }
  };

  const handleFormDragEnd = () => {
    if (animationFrameFormRef.current !== null) {
      cancelAnimationFrame(animationFrameFormRef.current);
      animationFrameFormRef.current = null;
      flushFormWidth();
    }
    formDragRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", handleFormDrag);
    document.removeEventListener("mouseup", handleFormDragEnd);
  };

  const onFormHandleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    formDragRef.current = {
      startX: event.clientX,
      initialWidth: formWidthMotion.get(),
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleFormDrag);
    document.addEventListener("mouseup", handleFormDragEnd);
  };

  const handleMapDrag = (event: MouseEvent) => {
    if (!mapDragRef.current) {
      return;
    }

    const { startX, initialWidth } = mapDragRef.current;
    const delta = computeWidthDelta(startX, event.clientX);
    const formWidth = formWidthMotion.get();
    const maxMapWidth = getMaxContentWidth() - formWidth - totalHandleWidth;

    pendingMapWidthRef.current = Math.max(
      minSecondaryWidth,
      Math.min(maxMapWidth, initialWidth + delta),
    );
    if (animationFrameMapRef.current === null) {
      animationFrameMapRef.current = requestAnimationFrame(() => {
        flushMapWidth();
        emitMapResize();
      });
    }
  };

  const handleMapDragEnd = () => {
    if (animationFrameMapRef.current !== null) {
      cancelAnimationFrame(animationFrameMapRef.current);
      animationFrameMapRef.current = null;
      flushMapWidth();
      emitMapResize();
    }
    mapDragRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", handleMapDrag);
    document.removeEventListener("mouseup", handleMapDragEnd);
  };

  const onMapHandleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    mapDragRef.current = {
      startX: event.clientX,
      initialWidth: mapWidthMotion.get(),
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMapDrag);
    document.addEventListener("mouseup", handleMapDragEnd);
  };

  const onFormHandleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }
    event.preventDefault();

    const mapWidth = mapWidthMotion.get();
    const maxFormWidth = getMaxContentWidth() - mapWidth - totalHandleWidth;
    const isExpand = isRtl
      ? event.key === "ArrowRight"
      : event.key === "ArrowLeft";

    formWidthMotion.set(
      Math.max(
        minFormWidth,
        Math.min(
          maxFormWidth,
          formWidthMotion.get() +
            (isExpand ? resizeKeyboardStep : -resizeKeyboardStep),
        ),
      ),
    );
  };

  const onMapHandleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      return;
    }
    event.preventDefault();

    const formWidth = formWidthMotion.get();
    const maxMapWidth = getMaxContentWidth() - formWidth - totalHandleWidth;
    const isExpand = isRtl
      ? event.key === "ArrowRight"
      : event.key === "ArrowLeft";

    const newWidth = Math.max(
      minSecondaryWidth,
      Math.min(
        maxMapWidth,
        mapWidthMotion.get() +
          (isExpand ? resizeKeyboardStep : -resizeKeyboardStep),
      ),
    );
    mapWidthMotion.set(newWidth);
    emitMapResize();
  };

  const gridStyle: MotionStyle = useMemo(
    () => ({
      gridTemplateColumns: gridTemplateColumnsStyle,
    }),
    [gridTemplateColumnsStyle],
  );

  return {
    gridStyle,
    drawerWidthStyle,
    isMapVisible,
    onFormHandleMouseDown,
    onFormHandleKeyDown,
    onMapHandleMouseDown,
    onMapHandleKeyDown,
  };
};
